import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageCircle, MessagesSquare, Image as ImageIcon, Clapperboard, Megaphone,
  Palette, Phone, Users, Languages, Clock, ShieldCheck, Sparkles,
} from 'lucide-react';

// ── CSS global injecté ────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800;900&family=Space+Grotesk:wght@700;800&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { overflow-x: hidden; }

  @keyframes fadeUp  { from { opacity:0; transform:translateY(28px); } to { opacity:1; transform:translateY(0); } }
  @keyframes heroIn  { from { opacity:0; transform:translateY(40px); } to { opacity:1; transform:translateY(0); } }
  @keyframes pulse2  { 0%,100% { opacity:0.4; transform:scale(1); } 50% { opacity:0.9; transform:scale(1.08); } }
  @keyframes spin360 { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
  @keyframes floatY  { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-14px); } }
  @keyframes gridScroll { from { transform:translateY(0); } to { transform:translateY(80px); } }
  @keyframes bubbleIn { from { opacity:0; transform:translateY(10px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }
  @keyframes dotBounce { 0%,60%,100% { transform:translateY(0); opacity:0.4; } 30% { transform:translateY(-4px); opacity:1; } }

  .teckia-landing { font-family:'DM Sans','Segoe UI',Arial,sans-serif; }
  .hero-title {
    font-family:'Space Grotesk',sans-serif;
    font-size: clamp(38px,7vw,72px);
    font-weight:800; line-height:1.08; letter-spacing:-2px;
    animation: heroIn 0.9s ease both;
  }
  .hero-sub {
    font-size: clamp(15px,2.5vw,19px);
    color:rgba(255,255,255,0.5); line-height:1.7;
    animation: heroIn 0.9s ease 0.2s both;
  }
  .gradient-text {
    background: linear-gradient(120deg,#2dd4c8,#7c6fee 55%,#a855f7);
    -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text;
  }
  .badge {
    display:inline-flex; align-items:center; gap:8px;
    background:rgba(45,212,200,0.12); border:1px solid rgba(45,212,200,0.3);
    color:#2dd4c8; font-size:11px; font-weight:800; letter-spacing:1.2px;
    padding:8px 18px; border-radius:100px; text-transform:uppercase;
  }
  .cta-primary {
    display:inline-flex; align-items:center; gap:10px;
    background:linear-gradient(135deg,#2dd4c8,#a855f7);
    color:#fff; font-weight:800; font-size:16px;
    padding:16px 36px; border-radius:16px; border:none; cursor:pointer;
    text-decoration:none; transition:all 0.25s ease;
    box-shadow:0 8px 32px rgba(168,85,247,0.35);
    animation: heroIn 0.9s ease 0.4s both;
  }
  .cta-primary:hover { transform:translateY(-3px) scale(1.02); box-shadow:0 16px 48px rgba(168,85,247,0.5); }
  .cta-outline {
    display:inline-flex; align-items:center; gap:8px;
    color:rgba(255,255,255,0.55); font-size:15px; font-weight:600;
    padding:16px 24px; border-radius:16px;
    border:1px solid rgba(255,255,255,0.1);
    background:rgba(255,255,255,0.04); text-decoration:none;
    transition:all 0.25s ease; cursor:pointer;
    animation: heroIn 0.9s ease 0.5s both;
  }
  .cta-outline:hover { background:rgba(255,255,255,0.08); color:#fff; border-color:rgba(255,255,255,0.2); }
  .feat-card {
    background:rgba(255,255,255,0.03);
    border-radius:24px; padding:30px 26px;
    position:relative; overflow:hidden;
    animation: fadeUp 0.6s ease both;
    transition:transform 0.3s ease, box-shadow 0.3s ease;
    cursor:default;
  }
  .feat-card:hover { transform:translateY(-6px); }
  .step-row {
    display:flex; align-items:center; gap:28px;
    background:rgba(255,255,255,0.03);
    border-radius:20px; padding:24px 28px;
    animation: fadeUp 0.6s ease both;
    transition:all 0.3s ease;
    cursor:default;
  }
  .stat-card {
    text-align:center; padding:32px 20px;
    border-radius:24px; border:1px solid rgba(255,255,255,0.07);
    background:rgba(255,255,255,0.03);
  }
  .nav-link {
    color:rgba(255,255,255,0.5); font-size:14px; font-weight:600;
    text-decoration:none; transition:color 0.2s;
  }
  .nav-link:hover { color:#fff; }
  .footer-link {
    color:rgba(255,255,255,0.35); font-size:13px; font-weight:600;
    text-decoration:none; transition:color 0.2s;
  }
  .footer-link:hover { color:#2dd4c8; }
  .hero-actions { display:flex; gap:14px; justify-content:center; flex-wrap:wrap; }
  .channel-chip {
    display:inline-flex; align-items:center; gap:8px;
    background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08);
    color:rgba(255,255,255,0.6); font-size:13px; font-weight:700;
    padding:10px 18px; border-radius:100px;
  }
  .chat-mockup {
    width:100%; max-width:400px; margin:0 auto;
    background:rgba(255,255,255,0.04); backdrop-filter:blur(20px);
    border:1px solid rgba(255,255,255,0.1); border-radius:24px;
    padding:20px; text-align:left;
    box-shadow:0 30px 80px rgba(0,0,0,0.5);
    animation: heroIn 0.9s ease 0.5s both, floatY 6s ease infinite;
  }
  .chat-bubble-user, .chat-bubble-bot {
    max-width:82%; padding:11px 16px; border-radius:16px;
    font-size:13.5px; line-height:1.5; margin-bottom:10px;
    animation: bubbleIn 0.5s ease both;
  }
  .chat-bubble-user {
    background:rgba(255,255,255,0.08); color:rgba(255,255,255,0.85);
    border-bottom-left-radius:4px; margin-right:auto;
  }
  .chat-bubble-bot {
    background:linear-gradient(135deg,#2dd4c8,#a855f7); color:#fff;
    border-bottom-right-radius:4px; margin-left:auto; font-weight:600;
  }
  .typing-dots { display:inline-flex; gap:4px; padding:4px 2px; }
  .typing-dots span {
    width:5px; height:5px; border-radius:50%; background:rgba(255,255,255,0.5);
    animation: dotBounce 1.2s ease infinite;
  }
  .typing-dots span:nth-child(2) { animation-delay:0.15s; }
  .typing-dots span:nth-child(3) { animation-delay:0.3s; }

  @media(max-width:768px) {
    .feat-grid  { grid-template-columns:1fr 1fr !important; }
    .stats-grid { grid-template-columns:repeat(2,1fr) !important; }
    .hero-actions { flex-direction:column; align-items:stretch; }
    .cta-primary, .cta-outline { text-align:center; justify-content:center; }
    .nav-links { display:none !important; }
  }
  @media(max-width:480px) {
    .feat-grid { grid-template-columns:1fr !important; }
  }
`;

// ── Animated Counter ──────────────────────────────────────────────────────────
function Counter({ target, suffix, prefix }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const done = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !done.current) {
        done.current = true;
        const start = Date.now();
        const tick = () => {
          const p = Math.min((Date.now() - start) / 2000, 1);
          const ease = 1 - Math.pow(1 - p, 3);
          setCount(Math.floor(ease * target));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);
  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navBg = `rgba(3,3,12,${Math.min(scrollY / 80, 1) * 0.95})`;
  const navBorder = scrollY > 60 ? '1px solid rgba(255,255,255,0.06)' : '1px solid transparent';

  const channels = [
    { icon: MessageCircle, label: 'Messenger' },
    { icon: MessagesSquare, label: 'WhatsApp' },
    { icon: Megaphone, label: 'Commentaires Facebook' },
  ];

  const features = [
    { Icon: MessageCircle,   color: '#2dd4c8', title: 'Agent Messenger & WhatsApp', delay: '0s',
      desc: 'Répond automatiquement à vos clients, connaît vos prix et vos produits, gère les ventes et transfère à un humain si besoin.' },
    { Icon: Megaphone,       color: '#a855f7', title: 'Réponses aux commentaires', delay: '0.1s',
      desc: 'Détecte les commentaires sous vos publications et y répond publiquement, jour et nuit, sans intervention.' },
    { Icon: ImageIcon,       color: '#7c6fee', title: 'TeckiA Image Studio', delay: '0.2s',
      desc: 'Génère des visuels produits professionnels en quelques secondes, prêts à publier.' },
    { Icon: Clapperboard,    color: '#2dd4c8', title: 'TeckiA Vidéo Studio', delay: '0.3s',
      desc: 'Vidéos IA et storyboards scène par scène pour vos publicités et contenus réseaux sociaux.' },
    { Icon: Palette,         color: '#a855f7', title: 'Marketing Studio', delay: '0.4s',
      desc: 'Visuels publicitaires et essayage virtuel de vêtements pour mettre vos produits en scène.' },
    { Icon: Phone,           color: '#7c6fee', title: 'Réceptionniste IA vocal', delay: '0.5s',
      desc: 'Répond au téléphone et prend les appels de vos clients, 24h/24, sans file d’attente.' },
    { Icon: Users,           color: '#2dd4c8', title: 'Prise de main humaine', delay: '0.6s',
      desc: 'Votre équipe reprend la conversation à tout moment, l’IA se met en pause automatiquement.' },
    { Icon: Languages,       color: '#a855f7', title: 'Malgache natif', delay: '0.7s',
      desc: 'Comprend et répond naturellement en malgache et en français — pas de traduction approximative.' },
  ];

  const steps = [
    { num: '01', title: 'Demandez la connexion de votre page', desc: 'Notre équipe connecte votre Page Facebook ou WhatsApp pour vous — aucune configuration technique requise.', color: '#2dd4c8' },
    { num: '02', title: 'Configurez votre agent',              desc: 'Vos produits, vos prix, votre ton de réponse : tout se personnalise en quelques minutes depuis le dashboard.', color: '#7c6fee' },
    { num: '03', title: 'Votre IA travaille 24/7',              desc: 'Messages, commentaires, ventes, visuels marketing : elle s’en occupe pendant que vous gérez votre business.', color: '#a855f7' },
  ];

  const stats = [
    { val: 24,  suffix: '/7', label: 'Disponibilité de l’agent', color: '#2dd4c8' },
    { val: 2,   suffix: '',   label: 'Langues natives (FR/MG)', color: '#7c6fee' },
    { val: 7,   suffix: 'j',  label: 'Essai gratuit',           color: '#a855f7' },
    { val: 100, suffix: '%',  label: 'Géré par notre équipe',   color: '#2dd4c8' },
  ];

  return (
    <div className="teckia-landing" style={{ background: '#0b1220', color: '#fff', minHeight: '100vh', overflowX: 'hidden' }}>
      <style>{GLOBAL_CSS}</style>

      {/* ── NAV ── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        padding: '16px 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: navBg, backdropFilter: scrollY > 40 ? 'blur(20px)' : 'none',
        borderBottom: navBorder, transition: 'all 0.3s ease',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/teckia-logo.png" alt="TeckiA" style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover' }} />
          <span style={{ fontSize: 20, fontWeight: 900, letterSpacing: -0.5 }}>
            Tecki<span style={{ color: '#a855f7' }}>A</span>
          </span>
        </div>

        <div className="nav-links" style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          <a href="#features" className="nav-link">Fonctionnalités</a>
          <a href="#how"      className="nav-link">Comment ça marche</a>
          <a href="/privacy"  className="nav-link">Confidentialité</a>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={() => navigate('/login')}
            style={{
              background: 'transparent', color: '#fff', fontWeight: 700, fontSize: 14,
              padding: '10px 20px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.2)',
              cursor: 'pointer', transition: 'all 0.2s ease',
            }}>
            Se connecter
          </button>
          <button
            onClick={() => navigate('/signup')}
            style={{
              background: 'linear-gradient(135deg,#2dd4c8,#a855f7)',
              color: '#fff', fontWeight: 800, fontSize: 14,
              padding: '10px 24px', borderRadius: 12, border: 'none', cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(168,85,247,0.3)', transition: 'all 0.2s ease',
            }}>
            Essai gratuit →
          </button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section style={{
        minHeight: '100vh',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', padding: '140px 24px 80px',
        position: 'relative',
      }}>
        {/* Glow */}
        <div style={{
          position: 'absolute', top: '26%', left: '50%',
          transform: 'translate(-50%,-50%)',
          width: 700, height: 700,
          background: 'radial-gradient(circle,rgba(45,212,200,0.13) 0%,transparent 70%)',
          pointerEvents: 'none',
        }} />
        {/* Grid */}
        <div style={{
          position: 'absolute', inset: 0, overflow: 'hidden',
          backgroundImage: 'linear-gradient(rgba(45,212,200,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(45,212,200,0.05) 1px,transparent 1px)',
          backgroundSize: '80px 80px',
          animation: 'gridScroll 8s linear infinite',
          pointerEvents: 'none',
        }} />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: 780 }}>
          <div className="badge" style={{ marginBottom: 26, animation: 'heroIn 0.9s ease both' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#2dd4c8', animation: 'pulse2 2s ease infinite', display: 'inline-block' }} />
            Agent IA pour Facebook & WhatsApp · Madagascar
          </div>

          <h1 className="hero-title" style={{ marginBottom: 24 }}>
            L&apos;IA qui répond à vos clients<br />
            <span className="gradient-text">pendant que vous gérez votre business</span>
          </h1>

          <p className="hero-sub" style={{ maxWidth: 600, margin: '0 auto 40px' }}>
            TeckiA répond à vos messages et commentaires Facebook/WhatsApp, vend vos produits
            et crée vos visuels marketing — automatiquement, en français et en malgache.
          </p>

          <div className="hero-actions" style={{ marginBottom: 56 }}>
            <button className="cta-primary" onClick={() => navigate('/signup')}>
              ✦ Essai gratuit de 7 jours
            </button>
            <a href="#features" className="cta-outline">
              Voir les fonctionnalités ↓
            </a>
          </div>
        </div>

        {/* Chat mockup */}
        <div className="chat-mockup" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <img src="/teckia-logo.png" alt="TeckiA" style={{ width: 32, height: 32, borderRadius: 9, objectFit: 'cover' }} />
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 13, fontWeight: 800 }}>Votre Page Facebook</div>
              <div style={{ fontSize: 11, color: '#2dd4c8', fontWeight: 600 }}>● Agent TeckiA actif</div>
            </div>
          </div>
          <div className="chat-bubble-user" style={{ animationDelay: '0.6s' }}>
            Salama! Inona ny vidin&apos;ny formation développeur?
          </div>
          <div className="chat-bubble-bot" style={{ animationDelay: '1s' }}>
            Ny vidiny dia 150 000 Ar. Afaka manomboka ianao raha vao vonona 😊
          </div>
          <div className="chat-bubble-user" style={{ animationDelay: '1.4s', marginBottom: 0 }}>
            <span className="typing-dots"><span /><span /><span /></span>
          </div>
        </div>

        {/* Channels */}
        <div style={{ marginTop: 40, display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', position: 'relative', zIndex: 1, animation: 'heroIn 0.9s ease 0.7s both' }}>
          {channels.map((c, i) => (
            <div key={i} className="channel-chip">
              <c.icon size={15} strokeWidth={2.4} />
              {c.label}
            </div>
          ))}
        </div>
      </section>

      {/* ── STATS ── */}
      <section style={{ padding: '40px 24px 60px', maxWidth: 1100, margin: '0 auto' }}>
        <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
          {stats.map((s, i) => (
            <div key={i} className="stat-card" style={{ animationDelay: `${i * 0.1}s` }}>
              <div style={{
                fontSize: 38, fontWeight: 900, color: s.color, marginBottom: 8,
                fontFamily: "'Space Grotesk',sans-serif",
              }}>
                <Counter target={s.val} suffix={s.suffix} prefix={s.prefix} />
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" style={{ padding: '80px 24px', maxWidth: 1180, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <div className="badge" style={{ marginBottom: 20 }}>Fonctionnalités</div>
          <h2 style={{
            fontFamily: "'Space Grotesk',sans-serif",
            fontSize: 'clamp(30px,5vw,52px)', fontWeight: 800, letterSpacing: -1, marginBottom: 16,
          }}>
            Toute votre présence digitale,<br /><span className="gradient-text">gérée par une seule IA</span>
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 16, maxWidth: 560, margin: '0 auto' }}>
            Du premier message client jusqu&apos;au visuel publicitaire — TeckiA couvre toute la chaîne.
          </p>
        </div>

        <div className="feat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 18 }}>
          {features.map((f, i) => (
            <div key={i} className="feat-card" style={{
              border: `1px solid ${f.color}33`,
              animationDelay: f.delay,
            }}>
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                background: `linear-gradient(90deg,transparent,${f.color},transparent)`,
              }} />
              <div style={{
                width: 52, height: 52, borderRadius: 15,
                background: `${f.color}18`, border: `1px solid ${f.color}44`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 18,
              }}>
                <f.Icon size={24} color={f.color} strokeWidth={2.2} />
              </div>
              <h3 style={{ fontSize: 16, fontWeight: 800, color: '#fff', marginBottom: 8 }}>{f.title}</h3>
              <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.5)', lineHeight: 1.65, margin: 0 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how" style={{ padding: '80px 24px', maxWidth: 900, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <div className="badge" style={{ marginBottom: 20 }}>Comment ça marche</div>
          <h2 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 'clamp(30px,5vw,52px)', fontWeight: 800, letterSpacing: -1 }}>
            Opérationnel en <span className="gradient-text">quelques minutes</span>
          </h2>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {steps.map((step, i) => (
            <div key={i} className="step-row" style={{
              border: `1px solid ${step.color}22`,
              animationDelay: `${i * 0.15}s`,
            }}>
              <div style={{
                fontFamily: "'Space Grotesk',sans-serif",
                fontSize: 48, fontWeight: 900, color: `${step.color}33`,
                flexShrink: 0, lineHeight: 1, minWidth: 70,
              }}>{step.num}</div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginBottom: 6 }}>{step.title}</h3>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, lineHeight: 1.6, margin: 0 }}>{step.desc}</p>
              </div>
              <div style={{
                width: 48, height: 48, borderRadius: 14,
                background: `${step.color}18`, border: `1px solid ${step.color}44`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, flexShrink: 0, color: step.color,
              }}>→</div>
            </div>
          ))}
        </div>

        {/* Trust row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center', marginTop: 48 }}>
          {[
            { Icon: ShieldCheck, text: 'Service géré par notre équipe' },
            { Icon: Sparkles,    text: 'Paiement MVola, Orange Money ou Carte' },
            { Icon: Clock,       text: 'Sans engagement' },
          ].map((t, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.4)', fontSize: 13, fontWeight: 600 }}>
              <t.Icon size={16} color="#2dd4c8" strokeWidth={2.2} />
              {t.text}
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section style={{ padding: '80px 24px 120px', textAlign: 'center' }}>
        <div style={{
          maxWidth: 700, margin: '0 auto',
          background: 'linear-gradient(135deg,rgba(45,212,200,0.1),rgba(168,85,247,0.08))',
          border: '1px solid rgba(45,212,200,0.2)',
          borderRadius: 32, padding: '64px 40px',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
            width: 500, height: 500,
            background: 'radial-gradient(circle,rgba(168,85,247,0.1) 0%,transparent 70%)',
            pointerEvents: 'none',
          }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ fontSize: 56, marginBottom: 20, animation: 'floatY 3s ease infinite' }}>✦</div>
            <h2 style={{
              fontFamily: "'Space Grotesk',sans-serif",
              fontSize: 'clamp(26px,4vw,44px)', fontWeight: 800, letterSpacing: -1, marginBottom: 16,
            }}>
              Essayez TeckiA gratuitement
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 16, marginBottom: 40, lineHeight: 1.6 }}>
              7 jours d&apos;essai gratuit sur votre Page Facebook. Aucune carte requise pour démarrer.
            </p>
            <div className="hero-actions">
              <button className="cta-primary" onClick={() => navigate('/signup')}>
                ✦ Démarrer mon essai gratuit
              </button>
              <a href="https://play.google.com/store" className="cta-outline" target="_blank" rel="noreferrer">
                ↓ App de gestion Android
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{
        borderTop: '1px solid rgba(255,255,255,0.06)',
        padding: '40px 32px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/teckia-logo.png" alt="TeckiA" style={{ width: 36, height: 36, borderRadius: 9, objectFit: 'cover' }} />
          <span style={{ fontSize: 18, fontWeight: 900 }}>
            Tecki<span style={{ color: '#a855f7' }}>A</span>
          </span>
        </div>

        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', justifyContent: 'center' }}>
          <a href="/privacy" className="footer-link">Politique de Confidentialité</a>
          <a href="/terms"   className="footer-link">CGU</a>
          <a href="mailto:contact@tdidigital.io" className="footer-link">Contact</a>
          <a href="https://tdidigital.io" className="footer-link" target="_blank" rel="noreferrer">TDI Digital</a>
        </div>

        <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: 12 }}>
          © 2026 TDI Digital LLC · Tous droits réservés
        </p>
      </footer>
    </div>
  );
}
